import { useCallback, useMemo } from 'react';
import {
  useLazyQualityDetails,
  useQualityDetails,
  type QualityDetailItem,
  type QualityDetailsParams,
  type QualityDetailsStats,
} from '@/entities/dashboards';

/**
 * Размер окна выдачи списка ячейки. Строка списка несёт результаты по всем критериям выборки,
 * поэтому окно заметно меньше серверного потолка.
 */
const DRILL_DETAILS_PAGE_SIZE = 100;

/** Заглавное окно: из него нужны только итоги ячейки и её размер, а не сами строки. */
const HEAD_QUERY_LIMIT = 1;

/** Условия отбора списка ячейки — всё, кроме самого окна выдачи. */
export type DrillDetailsQuery = Omit<QualityDetailsParams, 'limit' | 'offset'>;

/** Список звонков ячейки окнами выдачи и итоги всей ячейки. */
export type DrillDetailsPages = {
  /** Ячейка ещё не отвечена: у смены ячейки — вместо итогов и строк прежней */
  isLoading: boolean;
  /** Число звонков всей ячейки: им подписан список и по нему рассчитана прокрутка */
  totalCount: number;
  /** Итоги всей ячейки, а не показанных строк; `null` — в ячейке нет оценок с баллом */
  stats: QualityDetailsStats | null;
  /** Размер окна выдачи */
  pageSize: number;
  /** Сигнатура отбора: её смена начинает список заново */
  cacheVersion: string;
  /** Загрузка одного окна выдачи */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<QualityDetailItem[]>;
};

/**
 * Список звонков разобранной ячейки окнами выдачи: прокрутка доводит до последнего её звонка,
 * а не обрывается на первом окне, как было, пока список приходил одним куском.
 *
 * Размер ячейки известен до самих строк — он приходит заглавным окном в одну строку вместе с
 * итогами, которыми карточка подписана. Поэтому окна выдачи здесь считает сам грид от известного
 * ему числа записей, а не общий постраничный набор `useLazyGridPages`: тот нужен там, где размер
 * набора узнаётся только из первого пришедшего окна.
 *
 * `null` вместо условий отбора — ячейка не разобрана: запрос не уходит.
 */
export const useDrillDetailsPages = (query: DrillDetailsQuery | null): DrillDetailsPages => {
  const headQuery = useMemo(() => (query ? { ...query, limit: HEAD_QUERY_LIMIT } : undefined), [query]);

  const {
    data: headData,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = useQualityDetails(headQuery, { enabled: query !== null });

  const { refetch } = useLazyQualityDetails();

  const cacheVersion = useMemo(() => JSON.stringify(query), [query]);

  const fetchPage = useCallback(
    async (offset: number, limit: number) => {
      if (!query) return [];
      const data = await refetch({ ...query, offset, limit });
      return data.items;
    },
    [refetch, query],
  );

  // Без условий отбора ответ ячейки не запрашивался, а оставшийся от предыдущей описывает не её.
  const cellData = query ? headData : undefined;

  return {
    // Пока грузится ячейка — карточка ждёт её целиком, а не показывает итоги предыдущей рядом
    // со строками новой.
    isLoading: query !== null && (isLoading || (isFetching && isPlaceholderData)),
    totalCount: cellData?.total ?? 0,
    stats: cellData?.stats ?? null,
    pageSize: DRILL_DETAILS_PAGE_SIZE,
    cacheVersion,
    fetchPage,
  };
};
